/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: utils.ts (Queue Profiles Utilities)
 🗓️ Created: 2025-01-05
 🧩 Description:
    Utility functions for Queue Profiles module.
    Provides mapping, normalization, and transformation functions.

 🔧 Notes:
    - Type-safe transformations
    - Handles API response mapping
    - Simplified compared to Queues (no tiers)

 🔁 Last Updated: 2025-01-05 by Rodrigo Cuadra
────────────────────────────────────────────────────────────────*/

import {
  QueueProfileFormState,
  QueueProfile,
  MappedFormState,
} from '../queueProfiles/types';
import { KNOWN_SETTINGS_KEYS } from './constants';

export const mapResponseToState = (profile: QueueProfile): MappedFormState => {
  const settings =
    profile.settings && typeof profile.settings === 'object'
      ? (profile.settings as Record<string, unknown>)
      : {};

  const extraSettings: Record<string, unknown> = {};
  Object.entries(settings).forEach(([key, value]) => {
    if (!KNOWN_SETTINGS_KEYS.has(key)) {
      extraSettings[key] = value;
    }
  });

  const getStringSetting = (key: string, fallback = '') => {
    const value = settings[key];
    return typeof value === 'string' ? value : fallback;
  };

  const getBooleanSetting = (key: string, fallback = false) => {
    const value = settings[key];
    if (typeof value === 'boolean') return value;
    if (typeof value === 'string') {
      const normalized = value.trim().toLowerCase();
      return normalized === 'true' || normalized === '1' || normalized === 'yes';
    }
    if (typeof value === 'number') return value === 1;
    return fallback;
  };

  const getNumberSetting = (key: string, fallback = 0) => {
    const value = settings[key];
    if (typeof value === 'number') return value;
    if (typeof value === 'string') {
      const parsed = Number(value);
      return Number.isFinite(parsed) ? parsed : fallback;
    }
    return fallback;
  };

  const formState: QueueProfileFormState = {
    // General
    queueProfileName: profile.name ?? '',
    
    // Advanced Queue Logic
    memberDelay: String(getNumberSetting('member_delay', 2)),
    memberTimeout: String(getNumberSetting('member_timeout', 60)),
    truncateAgentsOnLoad: getBooleanSetting('truncate_agents_on_load', false),
    logDebugInfo: getBooleanSetting('log_debug_info', false),
    strictRetry: getBooleanSetting('strict_retry', false),
    skipAgentsWithActiveCall: getBooleanSetting('skip_agents_with_active_call', false),
    agentCooldownTime: String(getNumberSetting('agent_cooldown', 0)),
    agentBusyDelay: String(getNumberSetting('agent_busy_delay_time', 0)),
    agentRejectDelay: String(getNumberSetting('agent_reject_delay_time', 0)),
    maxWaitSize: String(getNumberSetting('max_wait_size', 0)),
    ringProgressively: getBooleanSetting('ring_progressively', false),
    skipCallerIdNumber: getStringSetting('skip_call_caller_id_number'),
    
    // Caller Experience / Announcements
    callerExitDestination: getStringSetting('caller_exit_destination'),
    ringbackTone: getStringSetting('ringback_tone', '${us-ring}') || '${us-ring}',
    announceWrapUpMessage: getStringSetting('announce_wrap_up_message'),
    announceResumePlayback: getBooleanSetting('announce_resume_playback', false),
    playRingbackBeforeMOH: getBooleanSetting('ringback_before_moh', false),
    agentWhisperTone: getStringSetting('agent_whisper'),
    
    // Tier Rules
    tierRulesApply: getBooleanSetting('tier_rules_apply', false),
    tierRuleWait: String(getNumberSetting('tier_rule_wait', 5)),
    tierRuleMultiplyLevel: getBooleanSetting('tier_rule_multiply_level', true),
    tierRuleNoAgentNoWait: getBooleanSetting('tier_rule_no_agent_no_wait', true),
    tierRuleMaxWait: String(getNumberSetting('tier_rule_max_wait', 0)),
    tierWaitStopSound: getStringSetting('tier_wait_stop_sound'),
    tierRuleLevelStopSound: getStringSetting('tier_rule_level_stop_sound'),
    tierSkipBusyAgents: getBooleanSetting('tier_skip_busy_agents', false),
    tierRandomizeAgents: getBooleanSetting('tier_randomize_agents', false),
    reserveAgents: getBooleanSetting('reserve_agents', false),
    truncateTiersOnLoad: getBooleanSetting('truncate_tiers_on_load', false),
    tierNoWait: getBooleanSetting('tier_no_wait', false),
    tierAgentNoWait: getBooleanSetting('tier_agent_no_wait', false),
    tierRetryWait: String(getNumberSetting('tier_retry_wait', 0)),
    tierRuleRetryWait: String(getNumberSetting('tier_rule_retry_wait', 0)),
    tierAgentRetryWait: String(getNumberSetting('tier_agent_retry_wait', 0)),
    
    createdAt: profile.createdAt ?? null,
    updatedAt: profile.updatedAt ?? null
  };

  return { formState, extraSettings };
};

